import Controller from "sap/ui/core/mvc/Controller";
import UIComponent from "sap/ui/core/UIComponent";
import Component from "../../Component";
import Model from "sap/ui/model/Model";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import Router from "sap/ui/core/routing/Router";
import History from "sap/ui/core/routing/History";
import ApiService from "../../services/ApiService";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Route$MatchedEvent } from "sap/ui/core/routing/Route";
import { Routes } from "../../constants/Routes";
import MessageType from "sap/ui/core/message/MessageType";
import MessageStrip from "sap/m/MessageStrip";
import { GetDynamicTranslation } from "../../utils/DynamicTranslationUtil";
import MessageBox from "sap/m/MessageBox";
import Fragment from "sap/ui/core/Fragment";
import Dialog from "sap/m/Dialog";
import {
  ApiGetArguments,
  ApiPostArguments,
} from "akademika/akamont/models/ApiArguments";
import { ApiResponse } from "akademika/akamont/models/ApiResponse";
import { ValueStateElement } from "akademika/akamont/models/ValueStateElement";
import { ValueState } from "sap/ui/core/library";
import { ButtonType, DialogType } from "sap/m/library";
import Text from "sap/m/Text";
import Button from "sap/m/Button";
import TextArea from "sap/m/TextArea";
import VBox from "sap/m/VBox";
import {
  FormatDate,
  FormatDateTime,
  FormatPrice,
  GetTranslationKeyForBoolean,
} from "akademika/akamont/utils/FormatUtil";
import { AppSettings } from "../../constants/AppSettings";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import { GetAccessTokenPayload } from "../../services/AuthService";

/**
 * @namespace akademika.akamont.pages
 */
export default abstract class BaseController extends Controller {
  /** necessary for "onRouteMatched" binding */
  route?: string;
  /** ui5 route parameters, set in BaseController for convenience */
  routeParams: Record<string, string> = {};

  /** parent route for "back" navigation, set in controllers */
  parentRoute?: string;
  /** whether navigation back goes to UI5 history first, if present, or this.parentRoute */
  navBackUsingHistoryFirst = false;
  /** parent params for "back" navigation, set in controllers */
  parentRouteParams?: Record<string, string>;
  public _oDialog: Dialog | null = null;
  private fieldsToClear: ValueStateElement[] = [];

  public onInit(): void {
    // allow controllers to have a callback method on route matched
    if (this.route) {
      const route = this.getRouter().getRoute(this.route);
      if (!route) {
        console.error(`route does not exist - ${this.route}`);
      } else {
        route.attachMatched(this._onRouteMatched, this);
      }
    }

    this.setModel(
      new JSONModel({ value: AppSettings.tableItemsThreshold }),
      "tableItemsThreshold"
    );
    this.setModel(
      new JSONModel({
        allowedTypes: AppSettings.files.allowedTypes,
        maxAllowedSize: AppSettings.files.maxAllowedSize,
      }),
      "fileSettings"
    );
  }

  protected _onRouteMatched(oEvent: Route$MatchedEvent) {
    this.routeParams =
      (oEvent.getParameter("arguments") as Record<string, string>) || {};
    this.hideMessage();
    this.clearFieldErrors();
    this.setModel(new JSONModel());
    this.onRouteMatched(oEvent);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onRouteMatched(oEvent: Route$MatchedEvent) {
    // to be implemented by controllers
  }

  /**
   * Convenience method for accessing the component of the controller's view.
   * @returns The component of the controller's view
   */
  public getOwnerComponent(): Component {
    return super.getOwnerComponent() as Component;
  }

  /**
   * Convenience method to get the components' router instance.
   * @returns The router instance
   */
  public getRouter(): Router {
    return UIComponent.getRouterFor(this);
  }

  /**
   * Convenience method for getting the i18n resource bundle of the component.
   * @returns The i18n resource bundle of the component
   */
  public getResourceBundle(): ResourceBundle | Promise<ResourceBundle> {
    const oModel = this.getOwnerComponent().getModel("i18n") as ResourceModel;
    return oModel.getResourceBundle();
  }

  /**
   * Convenience method for getting the view model by name in every controller of the application.
   * @param [sName] The model name
   * @returns The model instance
   */
  public getModel(sName?: string): JSONModel {
    const view = this.getView();
    if (!view) throw "No view";

    return view.getModel(sName) as JSONModel;
  }

  public getModelData<T>(sName?: string): T {
    return this.getModel(sName).getData() as T;
  }

  /**
   * Convenience method for setting the view model in every controller of the application.
   * @param oModel The model instance
   * @param [sName] The model name
   * @returns The current base controller instance
   */
  public setModel(oModel: Model, sName?: string): BaseController {
    const view = this.getView();
    if (!view) throw "No view";

    view.setModel(oModel, sName);
    return this;
  }

  /**
   * Convenience method for triggering the navigation to a specific target.
   * @public
   * @param sName Target name
   * @param [oParameters] Navigation parameters
   * @param [bReplace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
   */
  public navTo(sName: string, oParameters?: object, bReplace?: boolean): void {
    this.getRouter().navTo(sName, oParameters, undefined, bReplace);
  }

  /**
   * Convenience event handler for navigating back.
   * If parentRoute is set, we navigate to it.
   * If there is a history entry, we go one step back in the browser history.
   * If not, go to dashboard.
   */
  public onNavBack(): void {
    const sPreviousHash = History.getInstance().getPreviousHash();
    if (this.navBackUsingHistoryFirst && sPreviousHash) {
      window.history.go(-1);
    } else if (this.parentRoute) {
      this.navTo(this.parentRoute, this.parentRouteParams);
    } else {
      this.getRouter().navTo(Routes.DASHBOARD, {}, undefined, true);
    }
  }

  public getApiService(): ApiService {
    return this.getOwnerComponent().apiService;
  }

  public translate(key: string | null | undefined): string {
    if (!key) return "";
    return this.getOwnerComponent().getModel("i18n")?.getProperty(key) || key;
  }

  public translateBoolean(value?: boolean): string {
    return this.translate(GetTranslationKeyForBoolean(value));
  }

  public async translateWithParams(
    key: string,
    params: unknown[] = []
  ): Promise<string> {
    const i18nResourceBundle = await (
      this.getOwnerComponent().getModel("i18n") as ResourceModel
    )?.getResourceBundle();

    return i18nResourceBundle.getText(key, params) as string;
  }

  public translateDynamic(key: string): string {
    if (!key) return "";

    return GetDynamicTranslation(this.getOwnerComponent(), key);
  }

  public showMessage(
    message: string,
    type: MessageType = MessageType.Success,
    translate = true,
    fragmentId?: string
  ) {
    let messageControl = fragmentId
      ? (Fragment.byId(
        `${fragmentId}Dialog`,
        "messageFragment"
      ) as MessageStrip)
      : (this.getView()?.byId("messages") as MessageStrip);

    if (!messageControl) {
      console.error(
        `Could not find MessageStrip with id: "${fragmentId ? "messageFragment" : "messages"
        }"`
      );
      return;
    }

    messageControl.bindProperty(
      "text",
      translate ? { model: "i18n", path: message } : { value: message }
    );
    messageControl.setShowIcon(true);
    messageControl.setShowCloseButton(true);
    messageControl.setType(type);
    messageControl.addStyleClass("sapUiSmallMargin"); // TBD if need to check
    messageControl.setVisible(true);
  }

  public hideMessage(messageControlName = "messages") {
    const messageControl = this.getView()?.byId(
      messageControlName
    ) as MessageStrip;
    if (messageControl) {
      messageControl.setVisible(false);
    }
  }

  public async get<T = unknown>(endpoint: string, getArgs?: ApiGetArguments) {
    const rawResponse = await this.getApiService().get(endpoint, getArgs);
    return await this.parseRawResponse<T>(rawResponse, endpoint);
  }

  public async saveDataAndShowMessage<T = unknown>(
    endpoint: string,
    postArgs?: ApiPostArguments
  ): Promise<T> {
    const responseContent = await this.post<T>(endpoint, postArgs);
    this.showMessage("saveSuccess", MessageType.Success);
    return responseContent;
  }

  /**
   * Calls API endpoint, parses the response, assuming it's of type ApiResponse.
   * In case of success, returns inner content. In case of error, shows messages and throws error.
   */
  public async post<T = unknown>(
    endpoint: string,
    postArgs?: ApiPostArguments
  ) {
    this.clearFieldErrors(); // clear validation errors when re-submitting forms
    const rawResponse = await this.getApiService().post(endpoint, postArgs);
    return await this.parseRawResponse<T>(rawResponse, endpoint);
  }

  private async parseRawResponse<T = unknown>(
    rawResponse: Response,
    endpoint: string
  ): Promise<T> {
    if (!rawResponse.ok) {
      this.showHttpError(endpoint, rawResponse.status);
      throw "Error while calling URL " + endpoint;
    }

    const responseParsed = (await rawResponse.json()) as ApiResponse<T>;
    if (responseParsed.ok) {
      return responseParsed.content;
    } else {
      this.showApiResponseErrors(responseParsed, endpoint);

      throw "Error while calling URL " + endpoint;
    }
  }

  private showApiResponseErrors(
    apiResponse: ApiResponse,
    endpoint: string
  ): void {
    let aggregatedErrorMsg = "";
    if (apiResponse.messages?.length > 0) {
      for (const errorMsg of apiResponse.messages) {
        aggregatedErrorMsg += this.translate(errorMsg);
        aggregatedErrorMsg += "\n";
      }
    }

    if (apiResponse.fieldMessages && apiResponse.fieldMessages?.length > 0) {
      for (const fieldMsg of apiResponse.fieldMessages) {
        const fieldElt = this.byId(fieldMsg.id) as ValueStateElement;
        if (fieldElt) {
          if ("setValueState" in fieldElt) {
            fieldElt.setValueState(ValueState.Error);
            this.fieldsToClear.push(fieldElt);
          }
          if ("setValueStateText" in fieldElt) {
            fieldElt.setValueStateText(this.translate(fieldMsg.message));
          }
        } else {
          aggregatedErrorMsg +=
            fieldMsg.id + ": " + this.translate(fieldMsg.message) + "\n";
          console.error(
            `Could not find element with id "${fieldMsg.id}" to show validation error`
          );
        }
      }
    }

    if (aggregatedErrorMsg.length > 0) {
      this.showMessage(aggregatedErrorMsg, MessageType.Error, false);
      MessageBox.error(aggregatedErrorMsg);
    } else {
      this.showHttpError(endpoint);
    }
  }

  /** UI5 reuses the same view controls, so we have to manually clear validation errors */
  private clearFieldErrors() {
    for (let field of this.fieldsToClear) {
      field.setValueState(ValueState.None);
      if ("setValueStateText" in field) {
        field.setValueStateText("");
      }
    }
    this.fieldsToClear = [];
  }

  private async showHttpError(
    endpoint: string,
    status?: number
  ): Promise<void> {
    const errorMsg = await this.translateWithParams("fetchErrorMessage", [
      status || "",
      endpoint,
    ]);

    this.showMessage(errorMsg, MessageType.Error);
    MessageBox.error(errorMsg);
  }

  public async fetchModel<T = unknown>(
    endpoint: string,
    modelName: string | undefined = undefined,
    getArgs?: ApiGetArguments
  ): Promise<T> {
    const responseData = await this.get<T>(endpoint, getArgs);

    this.wipeModelData(modelName);
    this.setModel(new JSONModel(responseData as object), modelName);

    return responseData;
  }

  public async fetchModelPost<T = unknown>(
    endpoint: string,
    modelName: string | undefined = undefined,
    postArgs?: ApiPostArguments
  ): Promise<T> {
    const responseData = await this.post<T>(endpoint, postArgs);

    this.wipeModelData(modelName);
    this.setModel(new JSONModel(responseData as object), modelName);

    return responseData;
  }

  public async openDialog(
    fragmentName: string,
    isAbsolute = false
  ): Promise<Dialog> {
    const viewPath = this.getView()?.getViewName().split("/");
    if (!viewPath) throw "No viewpath found: " + viewPath;
    const fragmentPath = isAbsolute
      ? fragmentName
      : `${viewPath[0]}.${fragmentName}`;

    this._oDialog = (await Fragment.load({
      id: `${fragmentName}Dialog`,
      name: fragmentPath,
      controller: this,
    })) as Dialog;

    this.getView()?.addDependent(this._oDialog);

    return this._oDialog.open();
  }

  public closeDialog() {
    if (this._oDialog) {
      this._oDialog.close();
      this._oDialog.destroy();
      this._oDialog = null;
    }
  }

  public formatDateTime = FormatDateTime;

  public formatDate = FormatDate;

  public formatPrice = FormatPrice;

  public openSimpleDialog({
    title,
    message,
    state,
    type = "Default",
    confirmWithComment,
    confirmAction,
    cancelAction,
  }: {
    title: string;
    message: string;
    state: ValueState;
    type?: "Default" | "Confirm";
    confirmWithComment: boolean;
    confirmAction?: (comment?: string) => void;
    cancelAction?: () => void;
  }) {
    let commentTextArea: TextArea | undefined;

    const dialogContent = confirmWithComment
      ? new VBox({
        items: [
          new Text({ text: this.translate(message) }),
          (commentTextArea = new TextArea({
            placeholder: this.translate("comment"),
            width: "100%",
          }).addStyleClass("sapUiSmallMarginTop")),
        ],
      })
      : new Text({ text: this.translate(message) });

    this._oDialog = new Dialog({
      type: DialogType.Message,
      state: state,
      title: this.translate(title),
      content: dialogContent,
      beginButton: new Button({
        visible: type === "Confirm",
        type: ButtonType.Emphasized,
        text: this.translate("yes"),
        press: () => {
          const comment = commentTextArea?.getValue();
          if (confirmWithComment && !comment) {
            MessageBox.error(this.translate("messageRequiredForAction"));
            return;
          }

          this.closeDialog();
          if (confirmAction != null)
            confirmAction(confirmWithComment ? comment : undefined);
        },
      }),
      endButton: new Button({
        text:
          type === "Confirm"
            ? this.translate("cancel")
            : this.translate("close"),
        press: () => {
          this.closeDialog();
          if (cancelAction != null) cancelAction();
        },
      }),
    });

    this._oDialog.open();
  }

  public async postControlFilter(oEvent: Event, body?: any) {
    const params = oEvent?.getParameters ? oEvent.getParameters() : {};
    // @ts-expect-error filterURL/resolve/reject are provided via the custom event
    const { filterURL, resolve, reject, ...bodyParams } = params;

    if (!filterURL) {
      resolve?.(undefined);
      return;
    }

    try {
      const responseData = await this.post<any>(filterURL, {
        body: { ...bodyParams, ...body },
      });

      const source = oEvent.getSource() as Control;
      type BindingInfo = { model?: string; path?: string };
      const bindingInfo = source.getBindingInfo("items") as BindingInfo;

      source
        .getBinding("items")
        // @ts-expect-error UI5 runtime typing
        ?.getModel(bindingInfo?.model)
        // @ts-expect-error UI5 runtime typing
        ?.setProperty(bindingInfo?.path, responseData);

      resolve?.(responseData);
      return responseData;
    } catch (err) {
      reject?.(err);
      throw err;
    }
  }

  public SetMenuModelName() {
    const tokenPayload = GetAccessTokenPayload();
    if (tokenPayload) {
      this.getModel("navbarMenuModel").setProperty(
        "/givenName",
        tokenPayload.given_name
      );
    }
  }

  /**
    * Prevents loader from flashing.
    * @param fn Represents any block of code that might trigger rapid loader flag changes
    * @async
  */
  public async whileBusy<T>(fn: () => Promise<T>): Promise<T> {
    return await this.getOwnerComponent().whileBusy(fn);
  }

  /**
  * Wipes stale model data
  * @param modelName? Name of model to wipe the data from
*/
  public wipeModelData(modelName?: string) {
    const model = this.getModel(modelName);
    if (model instanceof JSONModel) {
      model.setData({});
    }
  }
}
